import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Crown, Zap, Shield, ArrowRight, CreditCard, ArrowLeft, Building2, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import { formatFeatureName } from '../../utils/featureNames';
import DirectCheckout from './DirectCheckout';
import { useCurrency } from '../../contexts/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const navigate = useNavigate();
  const { displayCurrency, formatCurrency } = useCurrency();
  const [subscriptionTiers, setSubscriptionTiers] = useState([]);
  const [systemFeatures, setSystemFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutDialog, setCheckoutDialog] = useState({
    isOpen: false,
    tier: null,
    billingCycle: 'monthly'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load tiers
      const tiersResponse = await axios.get(`${API}/api/subscription-tiers`);
      
      // Define tier order for proper sorting
      const tierOrder = { 'starter': 1, 'professional': 2, 'business': 3, 'enterprise': 4 };
      
      const activeTiers = tiersResponse.data
        .filter(tier => tier.is_active)
        .sort((a, b) => {
          // Sort by predefined order, or by price if not in predefined list
          const orderA = tierOrder[a.name?.toLowerCase()] || 99;
          const orderB = tierOrder[b.name?.toLowerCase()] || 99;
          if (orderA !== orderB) return orderA - orderB;
          // Fallback: sort by price (but handle -1 as highest)
          const priceA = a.price_monthly < 0 ? Infinity : a.price_monthly;
          const priceB = b.price_monthly < 0 ? Infinity : b.price_monthly;
          return priceA - priceB;
        });
      setSubscriptionTiers(activeTiers);
      
      // Load system features to get display names
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const featuresResponse = await axios.get(`${API}/api/admin/system-features`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSystemFeatures(featuresResponse.data.features || []);
        }
      } catch (err) {
        console.log('Could not load system features (not critical)');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(getErrorMessage(error, 'Failed to load pricing plans'));
    } finally {
      setLoading(false);
    }
  };

  // Get display name from system features or format it
  const getFeatureDisplayName = (featureName) => {
    // ALWAYS use formatFeatureName to ensure proper formatting
    // The snake_case feature name will be properly formatted
    return formatFeatureName(featureName);
  };

  const handleBackClick = () => {
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // No history, go to home
      navigate('/');
    }
  };

  // Format price using CurrencyContext (prices are stored in USD)
  const formatPrice = (usdPrice) => {
    return formatCurrency(usdPrice, 'USD', displayCurrency);
  };

  const handleSelectPlan = (tier) => {
    const billingCycle = isAnnual ? 'annual' : 'monthly';
    setCheckoutDialog({
      isOpen: true,
      tier: tier,
      billingCycle: billingCycle
    });
  };

  const handleCheckoutSuccess = (paymentData) => {
    setCheckoutDialog({ isOpen: false, tier: null, billingCycle: 'monthly' });
    toast.success('Payment successful! Welcome to TavPlans!');
    
    // Redirect to dashboard after successful payment
    setTimeout(() => {
      window.location.href = '/dashboard';
    }, 2000);
  };

  const handleCheckoutClose = () => {
    setCheckoutDialog({ isOpen: false, tier: null, billingCycle: 'monthly' });
  };

  const getTierIcon = (tierName) => {
    const name = tierName.toLowerCase();
    if (name.includes('starter') || name.includes('basic')) return Zap;
    if (name.includes('professional') || name.includes('pro')) return Crown;
    if (name.includes('business') || name.includes('team')) return Shield;
    if (name.includes('enterprise')) return Building2;
    return Zap;
  };

  // Check if tier is "Contact Sales" type (price is -1 or negative)
  const isContactSalesTier = (tier) => {
    return tier.price_monthly < 0 || tier.price_annual < 0;
  };

  const getTierColor = (index) => {
    const colors = [
      'border-blue-200 hover:border-blue-300',
      'border-purple-200 hover:border-purple-300 ring-2 ring-purple-100',
      'border-green-200 hover:border-green-300',
      'border-orange-200 hover:border-orange-300'
    ];
    return colors[index % colors.length];
  };

  const calculateSavings = (monthly, annual) => {
    const monthlyCost = monthly * 12;
    if (monthlyCost === 0) {
      return { savings: 0, percentage: 0 };
    }
    const savings = monthlyCost - annual;
    const percentage = Math.round((savings / monthlyCost) * 100);
    return { savings: isNaN(savings) ? 0 : savings, percentage: isNaN(percentage) ? 0 : percentage };
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-xl text-gray-600">Select the perfect plan for your business needs</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </CardHeader>
              <CardContent>
                <div className="h-12 bg-gray-200 rounded mb-4"></div>
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="h-4 bg-gray-200 rounded"></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <Button
        variant="outline"
        onClick={handleBackClick}
        className="mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
        <p className="text-xl text-gray-600 mb-8">
          Flexible pricing for teams of all sizes
        </p>
        
        {/* Billing Cycle Toggle */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <Label htmlFor="billing-toggle" className={!isAnnual ? 'font-semibold' : ''}>
            Monthly
          </Label>
          <Switch
            id="billing-toggle"
            checked={isAnnual}
            onCheckedChange={setIsAnnual}
          />
          <Label htmlFor="billing-toggle" className={isAnnual ? 'font-semibold' : ''}>
            Annual
          </Label>
          {subscriptionTiers.length > 0 && (() => {
            const savings = subscriptionTiers.map(tier => 
              calculateSavings(tier.price_monthly, tier.price_annual).percentage
            ).filter(p => p > 0);
            const maxSavings = savings.length > 0 ? Math.max(...savings) : 0;
            return maxSavings > 0 ? (
              <Badge variant="secondary" className="ml-2">
                Save up to {maxSavings}%
              </Badge>
            ) : null;
          })()}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
        {subscriptionTiers.map((tier, index) => {
          const Icon = getTierIcon(tier.name);
          const price = isAnnual ? tier.price_annual : tier.price_monthly;
          const originalPrice = isAnnual ? tier.price_monthly * 12 : null;
          const savings = isAnnual ? calculateSavings(tier.price_monthly, tier.price_annual) : null;
          const isPopular = index === 1; // Make second tier popular
          const isEnterprise = isContactSalesTier(tier);
          
          return (
            <Card key={tier.id} className={`relative transition-all duration-200 ${getTierColor(index)} ${isPopular ? 'scale-105' : ''} ${isEnterprise ? 'bg-gradient-to-b from-slate-50 to-slate-100' : ''}`}>
              {isPopular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center">
                <div className="flex justify-center mb-2">
                  <Icon className={`h-8 w-8 ${isEnterprise ? 'text-slate-700' : 'text-blue-600'}`} />
                </div>
                <CardTitle className="text-xl">{tier.display_name}</CardTitle>
                <CardDescription className="text-sm">
                  {tier.description}
                </CardDescription>
                
                <div className="mt-4">
                  {isEnterprise ? (
                    // Contact Sales display for Enterprise
                    <div className="flex flex-col items-center justify-center">
                      <span className="text-2xl font-bold text-slate-700">Custom Pricing</span>
                      <span className="text-sm text-slate-500 mt-1">Tailored to your needs</span>
                    </div>
                  ) : (
                    // Normal pricing display
                    <>
                      <div className="flex items-baseline justify-center">
                        <span className="text-3xl font-bold">{formatPrice(price)}</span>
                        <span className="text-gray-500 ml-1">
                          /{isAnnual ? 'year' : 'month'}
                        </span>
                      </div>
                      
                      {isAnnual && originalPrice && savings && savings.percentage > 0 && (
                        <div className="text-sm text-gray-500 mt-1">
                          <span className="line-through">{formatPrice(originalPrice)}/year</span>
                          <span className="text-green-600 ml-2">
                            Save {formatPrice(savings.savings)} ({savings.percentage}%)
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Trial Badge - only show for non-enterprise */}
                  {!isEnterprise && tier.trial_enabled && tier.trial_days > 0 && (
                    <div className="mt-2">
                      <Badge className="bg-green-500 text-white">
                        {tier.trial_days}-Day Free Trial
                      </Badge>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Features with custom display text */}
                <ul className="space-y-2 mb-6">
                  {tier.features && tier.features.length > 0 ? (
                    tier.features.map((feature, featureIndex) => {
                      // Use custom display text if available, otherwise generate default
                      let displayText = '';
                      
                      if (tier.feature_display_texts && tier.feature_display_texts[feature]) {
                        // Use the exact custom display text set by admin
                        displayText = tier.feature_display_texts[feature];
                      } else {
                        // Fallback: Generate display text from limit + feature name
                        const featureLimit = tier.limits && tier.limits[feature];
                        const featureName = getFeatureDisplayName(feature);
                        
                        if (featureLimit === -1) {
                          displayText = `Unlimited ${featureName}`;
                        } else if (typeof featureLimit === 'number' && featureLimit > 0) {
                          displayText = `${featureLimit} ${featureName}`;
                        } else {
                          displayText = featureName;
                        }
                      }
                      
                      return (
                        <li key={featureIndex} className="flex items-start gap-2 text-sm">
                          <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{displayText}</span>
                        </li>
                      );
                    })
                  ) : (
                    <li className="text-sm text-gray-500 italic">No features assigned</li>
                  )}
                </ul>
                
                {isEnterprise ? (
                  // Contact Sales button for Enterprise - redirects to Quote Calculator
                  <Button 
                    className="w-full bg-slate-700 hover:bg-slate-800"
                    onClick={() => navigate('/quote-calculator')}
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Get Custom Quote
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  // Normal purchase/trial button
                  <Button 
                    className="w-full"
                    variant={isPopular ? "default" : "outline"}
                    onClick={() => handleSelectPlan(tier)}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {tier.trial_enabled && tier.trial_days > 0 ? 'Start Free Trial' : 'Choose Plan'}
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Options */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Need Something Different?</h2>
        <p className="text-gray-600 mb-6">
          Looking for export credits, custom features, or enterprise solutions?
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button 
            variant="outline"
            onClick={() => {
              // TODO: Implement direct export credits purchase
              toast.info('Export credits purchase coming soon!');
            }}
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Buy Export Credits
          </Button>
          <Button variant="outline">
            Contact Sales
          </Button>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-semibold mb-2">Can I upgrade or downgrade anytime?</h3>
            <p className="text-gray-600 text-sm">
              Yes, you can change your plan at any time. Changes take effect immediately.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
            <p className="text-gray-600 text-sm">
              We accept all major credit cards through Stripe and Ethiopian payments through Chapa.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Is there a free trial?</h3>
            <p className="text-gray-600 text-sm">
              Yes, all plans come with a 14-day free trial. No credit card required.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">What happens if I exceed my limits?</h3>
            <p className="text-gray-600 text-sm">
              You can purchase additional credits or upgrade to a higher plan with more capacity.
            </p>
          </div>
        </div>
      </div>
      
      {/* Direct Checkout Dialog */}
      <DirectCheckout
        tier={checkoutDialog.tier}
        billingCycle={checkoutDialog.billingCycle}
        isOpen={checkoutDialog.isOpen}
        onClose={handleCheckoutClose}
        onSuccess={handleCheckoutSuccess}
      />
    </div>
  );
};

export default PricingPage;